import { adminApiClient } from './adminApiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Standard CRUD methods for a top-level resource (e.g. /admin/users). */
export interface CrudService<
  TEntity,
  TCreatePayload,
  TUpdatePayload,
  TId = string,
> {
  list(): Promise<{ data: TEntity[] }>;
  get(id: TId): Promise<{ data: TEntity }>;
  create(data: TCreatePayload): Promise<{ data: TEntity }>;
  update(id: TId, data: TUpdatePayload): Promise<{ data: TEntity }>;
  remove(id: TId): Promise<void>;
}

/** Standard CRUD methods for a nested resource (e.g. /admin/erp/registries/:parentId/endpoints). */
export interface NestedCrudService<
  TEntity,
  TCreatePayload,
  TUpdatePayload,
  TParentId = string,
  TId = string,
> {
  list(parentId: TParentId): Promise<{ data: TEntity[] }>;
  get(parentId: TParentId, id: TId): Promise<{ data: TEntity }>;
  create(parentId: TParentId, data: TCreatePayload): Promise<{ data: TEntity }>;
  update(parentId: TParentId, id: TId, data: TUpdatePayload): Promise<{ data: TEntity }>;
  remove(parentId: TParentId, id: TId): Promise<void>;
}

// ---------------------------------------------------------------------------
// Factory – top-level resource
// ---------------------------------------------------------------------------

/**
 * Creates a standard CRUD service object for a top-level API resource.
 *
 * @param basePath - The API path for the resource collection (e.g. `/admin/users`).
 */
export function createCrudService<
  TEntity,
  TCreatePayload,
  TUpdatePayload,
  TId = string,
>(basePath: string): CrudService<TEntity, TCreatePayload, TUpdatePayload, TId> {
  return {
    async list(): Promise<{ data: TEntity[] }> {
      const response = await adminApiClient.get<{ data: TEntity[] }>(basePath);
      return response.data;
    },

    async get(id: TId): Promise<{ data: TEntity }> {
      const response = await adminApiClient.get<{ data: TEntity }>(
        `${basePath}/${id}`,
      );
      return response.data;
    },

    async create(data: TCreatePayload): Promise<{ data: TEntity }> {
      const response = await adminApiClient.post<{ data: TEntity }>(
        basePath,
        data,
      );
      return response.data;
    },

    async update(id: TId, data: TUpdatePayload): Promise<{ data: TEntity }> {
      const response = await adminApiClient.put<{ data: TEntity }>(
        `${basePath}/${id}`,
        data,
      );
      return response.data;
    },

    async remove(id: TId): Promise<void> {
      await adminApiClient.delete(`${basePath}/${id}`);
    },
  };
}

// ---------------------------------------------------------------------------
// Factory – nested resource
// ---------------------------------------------------------------------------

/**
 * Creates a standard CRUD service object for a resource nested under a parent.
 *
 * @param buildBasePath - A function that returns the collection path given the parent ID
 *                        (e.g. `(registryId) => \`/admin/erp/registries/\${registryId}/endpoints\``).
 */
export function createNestedCrudService<
  TEntity,
  TCreatePayload,
  TUpdatePayload,
  TParentId = string,
  TId = string,
>(
  buildBasePath: (parentId: TParentId) => string,
): NestedCrudService<TEntity, TCreatePayload, TUpdatePayload, TParentId, TId> {
  return {
    async list(parentId: TParentId): Promise<{ data: TEntity[] }> {
      const response = await adminApiClient.get<{ data: TEntity[] }>(
        buildBasePath(parentId),
      );
      return response.data;
    },

    async get(parentId: TParentId, id: TId): Promise<{ data: TEntity }> {
      const response = await adminApiClient.get<{ data: TEntity }>(
        `${buildBasePath(parentId)}/${id}`,
      );
      return response.data;
    },

    async create(parentId: TParentId, data: TCreatePayload): Promise<{ data: TEntity }> {
      const response = await adminApiClient.post<{ data: TEntity }>(
        buildBasePath(parentId),
        data,
      );
      return response.data;
    },

    async update(
      parentId: TParentId,
      id: TId,
      data: TUpdatePayload,
    ): Promise<{ data: TEntity }> {
      const response = await adminApiClient.put<{ data: TEntity }>(
        `${buildBasePath(parentId)}/${id}`,
        data,
      );
      return response.data;
    },

    async remove(parentId: TParentId, id: TId): Promise<void> {
      await adminApiClient.delete(`${buildBasePath(parentId)}/${id}`);
    },
  };
}
